import { useEffect, useState } from "react";
import { getAnnualDates, saveAnnualDates, AnnualDateEntry } from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDisplay(iso: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[Number(m) - 1]} ${Number(d)} ${y}`;
}

function isActiveSeason(entry: AnnualDateEntry): boolean {
  const t = today();
  return t >= entry.startDate && t <= entry.endDate;
}

// VIP Cards can redeem offers until Dec 31 of the year the season ends, a grace period after the
// season itself (same rule as offerRedemptionEndDate on the server, which enforces it).
function redemptionEndDate(entry: AnnualDateEntry): string {
  return entry.endDate ? `${entry.endDate.slice(0, 4)}-12-31` : "";
}

// Members of the previous season can buy this season's card early, renewalDays before it starts
// (same rule as renewalWindow on the server).
const DEFAULT_RENEWAL_DAYS = 30;

function renewalDaysOf(entry: AnnualDateEntry): number {
  return entry.renewalDays ?? DEFAULT_RENEWAL_DAYS;
}

function renewalOpensOn(entry: AnnualDateEntry): string {
  const d = new Date(`${entry.startDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - renewalDaysOf(entry));
  return d.toISOString().slice(0, 10);
}

function inRedemptionGrace(entry: AnnualDateEntry): boolean {
  const t = today();
  return !!entry.endDate && t > entry.endDate && t <= redemptionEndDate(entry);
}

const emptyEntry = (): AnnualDateEntry => ({
  year: new Date().getFullYear() + 1,
  startDate: "",
  endDate: "",
});

const AnnualDates = () => {
  const [entries,  setEntries]  = useState<AnnualDateEntry[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState(false);
  const [hubdbWarning, setHubdbWarning] = useState<string | null>(null);
  const [adding,   setAdding]   = useState(false);
  const [newEntry, setNewEntry] = useState<AnnualDateEntry>(emptyEntry());

  useEffect(() => {
    getAnnualDates()
      .then(setEntries)
      .catch(() => setError("Failed to load annual dates."))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (updated: AnnualDateEntry[]) => {
    setError(null); setSuccess(false); setHubdbWarning(null); setSaving(true);
    try {
      const actor = getAdminSession()?.displayName || "Admin";
      const saved = await saveAnnualDates(updated, actor);
      setEntries(saved.entries);
      if (saved.hubdb && !saved.hubdb.synced) {
        setHubdbWarning(`Saved, but the HubSpot copy of these dates (HubDB) wasn't updated: ${saved.hubdb.message || "unknown error"}. Save again to retry.`);
      }
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (year: number) => {
    handleSave(entries.filter((e) => e.year !== year));
  };

  const handleAdd = () => {
    const { year, startDate, endDate } = newEntry;
    if (!year || !startDate || !endDate) { setError("All fields are required."); return; }
    if (endDate <= today()) { setError("End date must be in the future."); return; }
    if (startDate >= endDate) { setError("Start date must be before end date."); return; }
    if (entries.some((e) => e.year === year)) { setError(`Year ${year} already exists.`); return; }
    setError(null);
    const updated = [...entries, newEntry];
    handleSave(updated).then(() => { setAdding(false); setNewEntry(emptyEntry()); });
  };

  const handleUpdateField = (year: number, field: keyof AnnualDateEntry, value: string | number) => {
    setEntries((prev) => prev.map((e) => e.year === year ? { ...e, [field]: value } : e));
  };

  const handleSaveRow = (entry: AnnualDateEntry) => {
    if (!entry.startDate || !entry.endDate) { setError("Start and end date are required."); return; }
    if (entry.endDate <= today()) { setError("End date must be in the future."); return; }
    if (entry.startDate >= entry.endDate) { setError("Start date must be before end date."); return; }
    handleSave(entries.map((e) => e.year === entry.year ? entry : e));
  };

  return (
    <div style={{ padding: "2rem", maxWidth: 700 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.25rem" }}>
        <i className="bi bi-calendar-range" style={{ fontSize: "1.4rem", color: BRAND_GREEN }} />
        <h4 style={{ margin: 0, color: BRAND_GREEN, fontWeight: 700 }}>Annual Dates</h4>
      </div>
      <p className="text-muted small mb-4">
        Define the start and end dates for each membership year. The current season is determined automatically —
        if today falls within a configured range, that year is used. Otherwise the default Nov 1 – Oct 31 rule applies.
      </p>
      <p className="text-muted small mb-4" style={{ marginTop: "-0.75rem" }}>
        <i className="bi bi-ticket-perforated me-1" style={{ color: BRAND_GOLD }} />
        Golfers can keep redeeming offers until <strong>Dec 31</strong> of the year their season ends — e.g. a card
        bought any time in the 2027 season can redeem offers through Dec 31 2027.
      </p>
      <p className="text-muted small mb-4" style={{ marginTop: "-0.75rem" }}>
        <i className="bi bi-arrow-repeat me-1" style={{ color: BRAND_GOLD }} />
        Current members can renew early: for a set number of days before a season starts (30 by default), anyone
        who holds the season before it can buy the new card. They keep using this year's offers until Dec 31 and
        get the new year's offers on Jan 1.
      </p>

      {error   && <div className="alert alert-danger py-2">{error}</div>}
      {success && <div className="alert alert-success py-2">Saved successfully.</div>}
      {hubdbWarning && <div className="alert alert-warning py-2 small" style={{ overflowWrap: "anywhere" }}>{hubdbWarning}</div>}
      {loading && <p className="text-muted">Loading…</p>}

      {!loading && (
        <>
          {entries.length === 0 && !adding && (
            <p className="text-muted small">No custom date ranges configured. The default Nov 1 – Oct 31 rule is in effect.</p>
          )}

          {entries.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", marginBottom: "1.25rem" }}>
              {entries.map((entry) => {
                const active = isActiveSeason(entry);
                const grace  = inRedemptionGrace(entry);
                return (
                  <div
                    key={entry.year}
                    style={{
                      border: `1px solid ${active ? BRAND_GREEN : "#dee2e6"}`,
                      borderRadius: 8,
                      padding: "0.85rem 1rem",
                      background: active ? "rgba(2,56,34,0.04)" : "#fff",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 700, fontSize: "1rem", color: BRAND_GREEN, minWidth: 52 }}>
                        {entry.year}
                        {active && (
                          <span className="badge ms-2" style={{ background: BRAND_GREEN, color: "#fff", fontSize: "0.65rem", verticalAlign: "middle" }}>
                            Active
                          </span>
                        )}
                        {grace && (
                          <span className="badge ms-2" style={{ background: BRAND_GOLD, color: "#fff", fontSize: "0.65rem", verticalAlign: "middle" }}>
                            Offers still redeemable
                          </span>
                        )}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1, flexWrap: "wrap" }}>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          style={{ maxWidth: 160 }}
                          value={entry.startDate}
                          onChange={(e) => handleUpdateField(entry.year, "startDate", e.target.value)}
                        />
                        <span className="text-muted small">to</span>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          style={{ maxWidth: 160 }}
                          value={entry.endDate}
                          onChange={(e) => handleUpdateField(entry.year, "endDate", e.target.value)}
                        />
                        <span className="text-muted small" style={{ minWidth: 180, fontStyle: "italic" }}>
                          {entry.startDate && entry.endDate
                            ? `${formatDisplay(entry.startDate)} – ${formatDisplay(entry.endDate)}`
                            : ""}
                        </span>
                      </div>

                      <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button
                          className="btn btn-sm"
                          style={{ border: `1px solid ${BRAND_GREEN}`, color: BRAND_GREEN, fontSize: "0.78rem" }}
                          onClick={() => handleSaveRow(entry)}
                          disabled={saving}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: "0.78rem" }}
                          onClick={() => handleDelete(entry.year)}
                          disabled={saving}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </div>
                    {entry.endDate && (
                      <div className="small mt-2" style={{ color: "#6c757d" }}>
                        <i className="bi bi-ticket-perforated me-1" style={{ color: BRAND_GOLD }} />
                        Offers redeemable through <strong style={{ color: BRAND_GREEN }}>{formatDisplay(redemptionEndDate(entry))}</strong>
                      </div>
                    )}
                    {entry.startDate && (
                      <div className="small mt-1 d-flex align-items-center flex-wrap" style={{ color: "#6c757d", gap: "0.35rem" }}>
                        <i className="bi bi-arrow-repeat" style={{ color: BRAND_GOLD }} />
                        <span>{entry.year - 1} members can renew</span>
                        <input
                          type="number"
                          min={0}
                          max={120}
                          className="form-control form-control-sm"
                          style={{ width: 70 }}
                          value={renewalDaysOf(entry)}
                          onChange={(e) => handleUpdateField(entry.year, "renewalDays", Math.max(0, Math.min(120, Number(e.target.value) || 0)))}
                          aria-label={`Days before ${entry.year} starts that ${entry.year - 1} members can renew`}
                        />
                        <span>
                          days early
                          {renewalDaysOf(entry) > 0
                            ? <> — from <strong style={{ color: BRAND_GREEN }}>{formatDisplay(renewalOpensOn(entry))}</strong></>
                            : " (early renewal off)"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add new entry */}
          {adding ? (
            <div style={{ border: "1px dashed #adb5bd", borderRadius: 8, padding: "1rem", background: "#fafafa" }}>
              <div style={{ fontWeight: 600, color: BRAND_GREEN, marginBottom: "0.75rem" }}>New Season</div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <div>
                  <label className="form-label small mb-1">Year</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: 100 }}
                    value={newEntry.year}
                    min={2020}
                    max={2099}
                    onChange={(e) => setNewEntry((p) => ({ ...p, year: Number(e.target.value) }))}
                  />
                </div>
                <div>
                  <label className="form-label small mb-1">Start Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: 160 }}
                    value={newEntry.startDate}
                    onChange={(e) => setNewEntry((p) => ({ ...p, startDate: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="form-label small mb-1">End Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: 160 }}
                    value={newEntry.endDate}
                    onChange={(e) => setNewEntry((p) => ({ ...p, endDate: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.85rem" }}>
                <button
                  className="btn btn-sm"
                  style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none" }}
                  onClick={handleAdd}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Add Season"}
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => { setAdding(false); setNewEntry(emptyEntry()); setError(null); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              className="btn btn-sm"
              style={{ border: `1px solid ${BRAND_GOLD}`, color: BRAND_GOLD }}
              onClick={() => { setAdding(true); setError(null); }}
            >
              <i className="bi bi-plus-lg me-1" />
              Add Season
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AnnualDates;
